import React from 'react'
import {
  createInstance,
  LOG_LEVEL,
  OptimizelyUserContext,
} from '@optimizely/optimizely-sdk/dist/optimizely.lite.min.js'
import { WebTogglesWithOverrides } from '@dustin-web/microsite-config/src/web-toggles'
import { useSiteContext } from './use-site-context'
import { useAuth } from '../auth'
import { DigitalData } from '../utils/digital-data'
import { createEventDispatcher, createOptimizelyUserAttributes } from '../utils/optimizely-shared'
import { debounceWithArgs } from '../utils/debounce-with-args'

export const OptimizelyContext = React.createContext<{
  optimizely: ReturnType<typeof createInstance> | undefined
  hasAnalyticsConsent?: boolean
}>({ optimizely: undefined, hasAnalyticsConsent: false })

export const OptimizelyProvider = ({
  optimizely,
  hasAnalyticsConsent,
  children,
}: {
  optimizely: ReturnType<typeof createInstance> | undefined
  hasAnalyticsConsent?: boolean
  children: React.ReactNode
}) => {
  return (
    <OptimizelyContext.Provider value={{ optimizely, hasAnalyticsConsent }}>
      {children}
    </OptimizelyContext.Provider>
  )
}

let optimizelyClient: ReturnType<typeof createInstance> | undefined

export const getOrCreateOptimizelyClient = (datafile?: string, hasAnalyticsConsent = false) => {
  if (optimizelyClient) {
    return optimizelyClient
  }

  if (!datafile) {
    return undefined
  }

  const eventDispatcher = createEventDispatcher(hasAnalyticsConsent)

  optimizelyClient = createInstance({
    datafile: JSON.parse(datafile),
    eventDispatcher,
    clientEngine: 'javascript-sdk/lite',
    logLevel: LOG_LEVEL.WARNING,
  })

  return optimizelyClient
}

const debouncedPushEvent = debounceWithArgs(
  (payloadString: string) => {
    if (typeof window !== 'undefined') {
      const payload: Record<string, any> = JSON.parse(payloadString)
      DigitalData.pushEvent(payload)
    }
  },
  1000,
  { leading: true, trailing: false }
)

const debouncedDecide = debounceWithArgs(
  (user: OptimizelyUserContext | null, key: keyof WebTogglesWithOverrides) => {
    if (user) {
      return user.decide(key)
    }
    return null
  },
  1000,
  { leading: true, trailing: false }
)

export const useOptimizely = () => {
  const { optimizely, hasAnalyticsConsent } = React.useContext(OptimizelyContext)
  const { anonymousUserIdentifier, site, punchOutTicket, isMobile, deviceType, userAgent } =
    useSiteContext()
  const { userData } = useAuth()

  const user = React.useMemo(
    () =>
      optimizely?.createUserContext(
        anonymousUserIdentifier,
        createOptimizelyUserAttributes(
          site,
          punchOutTicket,
          isMobile,
          deviceType,
          userAgent,
          userData
        )
      ),
    [
      optimizely,
      anonymousUserIdentifier,
      site,
      punchOutTicket,
      isMobile,
      deviceType,
      userAgent,
      userData,
    ]
  )

  const optimizelyConfig = React.useMemo(() => optimizely?.getOptimizelyConfig(), [optimizely])

  const isToggleEnabled = React.useCallback(
    (key: keyof WebTogglesWithOverrides) => {
      if (optimizely && optimizelyConfig?.featuresMap?.[key] && user) {
        const decision = debouncedDecide(user, key)

        if (decision) {
          const { flagKey, ruleKey, variationKey } = decision
          const feature = optimizelyConfig.featuresMap[key]

          const isExperimentRule = feature?.experimentRules?.some(rule => rule.key === ruleKey)

          const experimentHasAllRequiredData =
            isExperimentRule && flagKey && ruleKey && variationKey

          if (hasAnalyticsConsent && experimentHasAllRequiredData) {
            const eventPayload = {
              event: 'optimizely-decision',
              'optimizely-flagKey': flagKey,
              'optimizely-ruleKey': ruleKey,
              'optimizely-variationKey': variationKey,
            }
            debouncedPushEvent(JSON.stringify(eventPayload))
          }
        }

        return decision?.enabled
      }
      return null
    },
    [optimizely, optimizelyConfig, user, hasAnalyticsConsent]
  )

  return {
    isToggleEnabled,
    optimizely,
    user,
  }
}
